import { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'

import { RoutingPaths } from 'app/routes/routes'

import { findExcursionsStore } from 'entities/excursions/models/find-excursions/find-excursions-store'
import { listExcursionStore } from 'entities/excursions/models/list-excursions/list-excursions-state'

import { isShowingMplus } from 'shared/constants'
import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll.ts'
import ImageGlass from 'shared/images/glasss.png'
import SmallGhostImage from 'shared/images/small-ghost.png'
import { getDurationValue } from 'shared/lib/excursions/getValueInLists'
import { CardSmallImage } from 'shared/ui/card-small-image'
import { EmptyResults } from 'shared/ui/empty-result'
import { ImageLazy } from 'shared/ui/image-lazy'
import { Loader } from 'shared/ui/loader'

import s from './styles.module.scss'

const EMPTY_PAGE = 0

const ListCard = observer(() => {
  const { t } = useTranslation()
  const targetRef = useRef<HTMLDivElement>(null!)

  const cardList = listExcursionStore.excursionsList

  const formSearchData = findExcursionsStore.findExcursions

  const navigate = useNavigate()

  const onClick = (index: number) => {
    const currentCard = cardList?.[index]

    navigate(RoutingPaths.excursion.guideDetail.path(String(currentCard?.id)))
  }

  useEffect(() => {
    if (!formSearchData?.departure_date?.length) {
      navigate(RoutingPaths.excursion.path())
    }
  }, [])

  useInfiniteScroll({
    targetRef,
    canLoad: listExcursionStore.page <= listExcursionStore.maxPage,
    onLoad: () => {
      const { filter, ...rest } = findExcursionsStore.findExcursions
      void listExcursionStore.onGetExcursions({ ...rest, ...filter })
    },
  })

  const listExcursions = useMemo(
    () =>
      cardList?.map((item) => ({
        title: item?.name || '',
        detail: `${item?.company_guide?.name} • ${item?.excursion_type.value} • ${t(getDurationValue(item?.duration))}`,
        price: item.is_promo ? item.new_price : item.price,
        image: item?.preview_image_fullpath,
        old_price: item?.price,
        is_promo: item?.is_promo,
        is_bonus: item?.is_bonus,
        new_price: item.new_price,
      })),
    [cardList],
  )

  return (
    <div className={s.container}>
      {listExcursionStore?.loading && (
        <Loader
          type="excursions"
          marginTop={200}
        />
      )}

      <div className={s.list}>
        {listExcursions?.map((excursion, index) => (
          <CardSmallImage
            key={nanoid()}
            {...excursion}
            isShowingMplus={isShowingMplus}
            onClick={() => onClick(index)}
            errorImage={SmallGhostImage}
            isPromo={excursion.is_promo}
            isMbonus={excursion.is_bonus}
            oldPrice={excursion.old_price}
            newPrice={excursion.new_price}
          />
        ))}
      </div>

      {listExcursions?.length === EMPTY_PAGE && !listExcursionStore.loading ? (
        <div className={s.empty}>
          <EmptyResults
            image={
              <ImageLazy
                src={ImageGlass}
                alt="empty"
                className={s.image}
              />
            }
            title={t('excursions.empty_title')}
            subTitle={t('excursions.empty_description')}
          />
        </div>
      ) : null}

      <div ref={targetRef} />
    </div>
  )
})

export default ListCard
